import { CRM, defaultLightTheme } from './root/CRM';
import { deepmerge } from '@mui/utils';
import { Theme } from '@mui/material/styles';
/**
 * Application entry point
 *
 * Customize Kosmojo HTP CRM by passing props to the CRM component:
 *  - contactGender
 *  - accountSectors
 *  - darkTheme
 *  - quoteCategories
 *  - quotePipelineStatuses
 *  - quoteStages
 *  - lightTheme
 *  - logo
 *  - noteStatuses
 *  - taskTypes
 *  - title
 * ... as well as all the props accepted by react-admin's <Admin> component.
 *
 * @example
 * const App = () => (
 *    <CRM
 *       logo="./img/logo.png"
 *       title="Acme CRM"
 *    />
 * );
 */

const customTheme = deepmerge(defaultLightTheme, {
    components: {
        MuiAppBar: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: '#FFFFFF',
                    color: '#0f172a', // slate-900
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#10b981', // emerald-500
                        height: 3,
                    },
                    '& .Mui-selected': {
                        color: '#047857 !important', // emerald-700
                        fontWeight: 500,
                    },
                    '& .MuiTab-root': {
                        color: '#475569', // slate-600
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    border: '1px solid #e2e8f0', // slate-200
                    borderRadius: '8px',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: (theme: Theme) =>
                            theme.palette.primary.main,
                    },
                    '&.MuiInputBase-root': {
                        '&.Mui-focused, &:has(.MuiSelect-select[aria-expanded="true"])':
                            {
                                backgroundColor: (theme: Theme) =>
                                    `${theme.palette.primary.main}1a`,
                                '.MuiSelect-select': {
                                    color: (theme: Theme) =>
                                        theme.palette.primary.main,
                                },
                            },
                        '&:has(.MuiSelect-select:not([value=""])': {
                            backgroundColor: (theme: Theme) =>
                                `${theme.palette.primary.main}1a`,
                            '.MuiSelect-select': {
                                color: (theme: Theme) =>
                                    theme.palette.primary.main,
                            },
                        },
                    },
                },
                icon: {
                    color: (theme: Theme) => theme.palette.primary.main,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: '#ecfdf5 !important', // emerald-50
                        color: '#047857 !important', // emerald-700
                    },
                    '&:hover': {
                        backgroundColor: '#f1f5f9', // slate-100
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: '#d1fae5 !important', // emerald-100
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        color: (theme: Theme) => theme.palette.primary.main,
                    },
                    '&.MuiFormLabel-filled': {
                        color: (theme: Theme) => theme.palette.primary.main,
                    },
                },
            },
        },
    },
});

const App = () => <CRM lightTheme={customTheme} />;

export default App;
